import { ArrowLeft, MapPin, Gauge, Calendar, Activity, AlertTriangle, Shield, Wrench, Droplet, Cog, Clock, Edit3, Plus, Package, BarChart3 } from 'lucide-react';
import { RadialBarChart, RadialBar, Legend, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { useState, useEffect } from 'react';
import EditVehicleModal from './EditVehicleModal';
import AddMaintenanceModal from './AddMaintenanceModal';
import VehicleMissionHistory from './VehicleMissionHistory';
import ServiceDueGraph from './ServiceDueGraph';
import CumulativeWearGraph from './CumulativeWearGraph';

const VehicleDetail = ({ vehicle, onBack, onVehicleUpdated }) => {
  const { theme, getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  
  // Log when component renders with vehicle data
  console.log('🔄 VehicleDetail rendering:', {
    vehicleId: vehicle.id,
    health: vehicle.health,
    components: vehicle.components,
    mileage: vehicle.mileage,
    timestamp: new Date().toISOString()
  });
  
  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  
  // Listen for mission status changes and refresh vehicle data
  useEffect(() => {
    const handleMissionStatusChange = async () => {
      console.log('🔔 VehicleDetail: Mission status changed - refreshing vehicle data');
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
        const response = await fetch(`${apiUrl}/api/fleet/vehicles/${vehicle.id}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
          }
        });
        
        if (response.ok) {
          const updatedVehicle = await response.json();
          console.log('✅ VehicleDetail: Vehicle data refreshed:', {
            health: updatedVehicle.health,
            components: updatedVehicle.components,
            mileage: updatedVehicle.mileage
          });
          
          if (onVehicleUpdated) {
            onVehicleUpdated(updatedVehicle);
          }
        }
      } catch (error) {
        console.error('❌ VehicleDetail: Failed to refresh vehicle data:', error);
      }
    };
    
    window.addEventListener('missionStatusChanged', handleMissionStatusChange);
    
    return () => {
      window.removeEventListener('missionStatusChanged', handleMissionStatusChange);
    };
  }, [vehicle.id, onVehicleUpdated]);
  
  // Load unit system from settings
  const savedSettings = localStorage.getItem('fracadapt-settings');
  let isMetric = true; // Default to Metric
  if (savedSettings) {
    try {
      const settings = JSON.parse(savedSettings);
      isMetric = settings.general?.unitSystem === 'Metric';
    } catch (error) {
      console.warn('Failed to load settings');
    }
  }
  
  const weightUnit = isMetric ? 'kg' : 'lbs';
  const distanceUnit = isMetric ? 'km' : 'mi';
  
  // Convert cargo weight from lbs (database) to current unit system
  const displayCargoWeight = vehicle.cargoWeight 
    ? isMetric 
      ? Math.round(vehicle.cargoWeight * 0.453592) 
      : vehicle.cargoWeight
    : null;
  
  // Convert mileage from mi (database) to current unit system
  const displayMileage = vehicle.mileage
    ? isMetric
      ? Math.round(vehicle.mileage * 1.60934)
      : vehicle.mileage
    : null;

  const handleVehicleUpdated = (updatedVehicle) => {
    if (onVehicleUpdated) {
      onVehicleUpdated(updatedVehicle);
    }
    setShowEditModal(false);
  };

  const handleMaintenanceAdded = async (newMaintenanceRecord) => {
    try {
      console.log('🔄 Handling maintenance added:', newMaintenanceRecord);
      
      // Close the modal first
      setShowMaintenanceModal(false);
      
      // Refetch the specific vehicle data from the server to get the latest maintenance records
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
      const response = await fetch(`${apiUrl}/api/fleet/vehicles/${vehicle.id}`);
      
      if (response.ok) {
        const updatedVehicle = await response.json();
        console.log('✅ Refreshed vehicle data from server, maintenance records:', updatedVehicle.maintenanceHistory.length);
        console.log('🔧 Updated component health:', updatedVehicle.components);
        console.log('📊 Updated overall health:', updatedVehicle.health);
        
        if (onVehicleUpdated) {
          onVehicleUpdated(updatedVehicle);
        }
      } else {
        console.log('❌ Failed to fetch updated data from server, using fallback');
        // Fallback to local state update if API fails
        const updatedVehicle = {
          ...vehicle,
          maintenanceHistory: [newMaintenanceRecord, ...vehicle.maintenanceHistory],
          lastMaintenance: newMaintenanceRecord.date,
        };
        
        if (onVehicleUpdated) {
          onVehicleUpdated(updatedVehicle);
        }
      }
    } catch (error) {
      console.error('Error refreshing vehicle data:', error);
      
      // Fallback to local state update
      console.log('🔄 Using fallback local state update');
      const updatedVehicle = {
        ...vehicle,
        maintenanceHistory: [newMaintenanceRecord, ...vehicle.maintenanceHistory],
        lastMaintenance: newMaintenanceRecord.date,
      };
      
      if (onVehicleUpdated) {
        onVehicleUpdated(updatedVehicle);
      }
    }
  };
  // Prepare data for radial bar chart with safety checks
  // Use componentHealth if available (enhanced structure), otherwise fall back to components
  const componentHealth = vehicle.componentHealth || {};
  // Ensure components is always simple numbers, not objects (handle case where backend sends enhanced structure)
  let components = vehicle.components || { engine: 100, suspension: 100, brakes: 100, transmission: 100 };
  
  // Safety: Convert any object values in components to numbers (in case backend didn't convert properly)
  if (components && typeof components === 'object') {
    const safeComponents = {};
    for (const [key, value] of Object.entries(components)) {
      if (typeof value === 'object' && value !== null && 'health' in value) {
        safeComponents[key] = value.health || 100;
      } else if (typeof value === 'number') {
        safeComponents[key] = value;
      } else {
        safeComponents[key] = 100; // Default
      }
    }
    components = safeComponents;
  }
  
  // Helper to get health value (from enhanced structure or simple number)
  // Handles both old format (number) and new format (object), and also handles if components itself is the enhanced structure
  const getComponentHealth = (componentName) => {
    // First check componentHealth (enhanced structure)
    if (componentHealth[componentName]) {
      if (typeof componentHealth[componentName] === 'object' && componentHealth[componentName] !== null) {
        return componentHealth[componentName].health || 100;
      }
      if (typeof componentHealth[componentName] === 'number') {
        return componentHealth[componentName];
      }
    }
    // Fall back to components (might be enhanced structure or simple numbers)
    if (components[componentName]) {
      if (typeof components[componentName] === 'object' && components[componentName] !== null) {
        return components[componentName].health || 100;
      }
      if (typeof components[componentName] === 'number') {
        return components[componentName];
      }
    }
    return 100; // Default
  };
  
  // Helper to get service status
  const getServiceStatus = (componentName) => {
    if (componentHealth[componentName] && typeof componentHealth[componentName] === 'object') {
      return componentHealth[componentName].serviceStatus || 'normal';
    }
    return 'normal';
  };
  
  // Helper to get service level
  const getServiceLevel = (componentName) => {
    if (componentHealth[componentName] && typeof componentHealth[componentName] === 'object') {
      return componentHealth[componentName].serviceLevel || null;
    }
    return null;
  };
  
  // Helper to get distance since last service
  const getDistanceSinceService = (componentName) => {
    if (componentHealth[componentName] && typeof componentHealth[componentName] === 'object') {
      return componentHealth[componentName].distanceSinceLastService || 0;
    }
    return 0;
  };
  
  // Get numeric health values for chart (ensure we always have numbers, not objects)
  const engineHealth = getComponentHealth('engine');
  const suspensionHealth = getComponentHealth('suspension');
  const brakesHealth = getComponentHealth('brakes');
  const transmissionHealth = getComponentHealth('transmission');
  
  const chartData = [
    {
      name: 'Engine',
      value: engineHealth,
      fill: engineHealth >= 80 ? '#10b981' : engineHealth >= 60 ? '#f59e0b' : '#ef4444'
    },
    {
      name: 'Suspension',
      value: suspensionHealth,
      fill: suspensionHealth >= 80 ? '#10b981' : suspensionHealth >= 60 ? '#f59e0b' : '#ef4444'
    },
    {
      name: 'Brakes',
      value: brakesHealth,
      fill: brakesHealth >= 80 ? '#10b981' : brakesHealth >= 60 ? '#f59e0b' : '#ef4444'
    },
    {
      name: 'Transmission',
      value: transmissionHealth,
      fill: transmissionHealth >= 80 ? '#10b981' : transmissionHealth >= 60 ? '#f59e0b' : '#ef4444'
    }
  ];

  // Get component health color
  const getHealthColor = (health) => {
    if (health >= 80) return 'text-green-400';
    if (health >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const getHealthBgColor = (health) => {
    if (health >= 80) return 'bg-green-600/20 border-green-500/30';
    if (health >= 60) return 'bg-amber-600/20 border-amber-500/30';
    return 'bg-red-600/20 border-red-500/30';
  };

  // Get alert badge color
  const getAlertBadgeColor = (level) => {
    switch (level) {
      case 'high':
        return 'bg-red-500 text-white';
      case 'medium':
        return 'bg-amber-500 text-white';
      case 'low':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };
  
  // Get service status badge styling
  const getServiceStatusBadge = (serviceStatus, serviceLevel) => {
    if (serviceStatus === 'normal') {
      return null; // No badge for normal status
    }
    
    const badgeClasses = {
      serviceDue: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
      serviceRequired: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      failureRisk: 'bg-red-500/20 text-red-400 border-red-500/50'
    };
    
    const levelLabels = {
      'Recommended': 'Service Due',
      'Critical': 'Service Required',
      'Failure Risk': 'Failure Risk'
    };
    
    return {
      className: badgeClasses[serviceStatus] || 'bg-gray-500/20 text-gray-400 border-gray-500/50',
      label: levelLabels[serviceLevel] || serviceStatus
    };
  };
  
  // Format distance for display
  const formatDistance = (km) => {
    if (isMetric) {
      return `${Math.round(km)} km`;
    } else {
      return `${Math.round(km * 0.621371)} mi`;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get maintenance icon based on details
  const getMaintenanceIcon = (details) => {
    const lowerDetails = details.toLowerCase();
    if (lowerDetails.includes('oil') || lowerDetails.includes('fluid')) {
      return <Droplet size={20} className="text-blue-400" />;
    } else if (lowerDetails.includes('brake')) {
      return <Shield size={20} className="text-amber-400" />;
    } else if (lowerDetails.includes('engine')) {
      return <Cog size={20} className="text-red-400" />;
    } else if (lowerDetails.includes('transmission')) {
      return <Cog size={20} className="text-purple-400" />;
    } else if (lowerDetails.includes('suspension')) {
      return <Activity size={20} className="text-green-400" />;
    } else {
      return <Wrench size={20} className="text-gray-400" />;
    }
  };

  // Custom tooltip for radial chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const tooltipBg = theme === 'light' ? 'bg-white border border-gray-300 shadow-lg' : 'bg-black/90 border border-military-700/50';
      const textPrimary = theme === 'light' ? 'text-gray-900' : 'text-white';
      const textAccent = theme === 'light' ? 'text-military-600' : 'text-blue-400';
      
      return (
        <div className={`${tooltipBg} rounded-lg p-3 shadow-xl`}>
          <p className={`${textPrimary} font-semibold`}>{payload[0].name}</p>
          <p className={`${textAccent} font-bold`}>{payload[0].value}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 overflow-x-hidden">
      {/* Back Button */}
      <button
        onClick={onBack}
        className={`flex items-center gap-2 ${themeClasses.textSecondary} hover:${themeClasses.textPrimary} mb-6 transition-colors`}
      >
        <ArrowLeft size={20} />
        <span>Back to Fleet Health</span>
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className={`text-3xl font-bold ${themeClasses.textPrimary}`}>{vehicle.model}</h2>
            <p className={`${themeClasses.textMuted} mt-1`}>{vehicle.brand}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <Edit3 size={16} />
              Edit Vehicle
            </button>
            <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${getAlertBadgeColor(vehicle.alertLevel)}`}>
              {vehicle.alertLevel.toUpperCase()} ALERT
            </span>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Side - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vehicle Specifications */}
          <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 shadow-md`}>
            <h3 className={`text-xl font-bold ${themeClasses.textPrimary} mb-4`}>Vehicle Specifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
              {/* Type */}
              <div className={`${themeClasses.secondary} rounded-lg p-4 border ${themeClasses.border}`}>
                <p className={`${themeClasses.textMuted} text-sm mb-1`}>Vehicle Type</p>
                <p className={`${themeClasses.textPrimary} text-lg font-semibold`}>{vehicle.type}</p>
              </div>

              {/* Year */}
              <div className={`${themeClasses.secondary} rounded-lg p-4 border ${themeClasses.border}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar size={16} className="text-purple-400" />
                  <p className={`${themeClasses.textMuted} text-sm`}>Year</p>
                </div>
                <p className={`${themeClasses.textPrimary} text-lg font-semibold`}>{vehicle.year}</p>
              </div>

              {/* Mileage */}
              <div className={`${themeClasses.secondary} rounded-lg p-4 border ${themeClasses.border}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Gauge size={16} className="text-blue-400" />
                  <p className={`${themeClasses.textMuted} text-sm`}>Mileage</p>
                </div>
                <p className={`${themeClasses.textPrimary} text-lg font-semibold`}>
                  {displayMileage ? displayMileage.toLocaleString() : 'N/A'} {distanceUnit}
                </p>
              </div>

              {/* Cargo Weight */}
              <div className={`${themeClasses.secondary} rounded-lg p-4 border ${themeClasses.border}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Package size={16} className="text-orange-400" />
                  <p className={`${themeClasses.textMuted} text-sm`}>Cargo Capacity</p>
                </div>
                <p className={`${themeClasses.textPrimary} text-lg font-semibold`}>
                  {displayCargoWeight ? displayCargoWeight.toLocaleString() : 'N/A'} {weightUnit}
                </p>
              </div>

              {/* Base */}
              <div className={`${themeClasses.secondary} rounded-lg p-4 border ${themeClasses.border}`}>
                <p className={`${themeClasses.textMuted} text-sm mb-1`}>Base</p>
                <p className={`${themeClasses.textPrimary} text-lg font-semibold`}>{vehicle.baseName}</p>
              </div>

              {/* Location */}
              <div className={`${themeClasses.secondary} rounded-lg p-4 border ${themeClasses.border}`}>
                <div className="flex items-center gap-2 mb-1">
                  <MapPin size={16} className="text-green-400" />
                  <p className={`${themeClasses.textMuted} text-sm`}>Location</p>
                </div>
                <p className={`${themeClasses.textPrimary} text-lg font-semibold`}>{vehicle.baseLocation}</p>
              </div>

              {/* Region */}
              <div className={`${themeClasses.secondary} rounded-lg p-4 border ${themeClasses.border}`}>
                <p className={`${themeClasses.textMuted} text-sm mb-1`}>Region</p>
                <p className={`${themeClasses.textPrimary} text-lg font-semibold`}>{vehicle.region}</p>
              </div>
            </div>
          </div>

          {/* Component Health Overview */}
          <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 shadow-md`}>
            <h3 className={`text-xl font-bold ${themeClasses.textPrimary} mb-4`}>Component Health Overview</h3>

            {/* Radial Bar Chart */}
            <div className="w-full h-96 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="10%"
                  outerRadius="90%"
                  data={chartData}
                  startAngle={180}
                  endAngle={-180}
                >
                  <RadialBar
                    minAngle={15}
                    background
                    clockWise
                    dataKey="value"
                    cornerRadius={10}
                  />
                  <Legend
                    iconSize={10}
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    wrapperStyle={{
                      paddingLeft: '20px',
                      fontSize: '14px',
                      color: '#fff'
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>

            {/* Component Percentage Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Engine */}
              {(() => {
                const health = getComponentHealth('engine');
                const serviceStatus = getServiceStatus('engine');
                const serviceLevel = getServiceLevel('engine');
                const distance = getDistanceSinceService('engine');
                const badge = getServiceStatusBadge(serviceStatus, serviceLevel);
                return (
                  <div className={`p-4 rounded-lg border ${getHealthBgColor(health)} relative`}>
                    <div className="flex flex-col items-center text-center">
                      <Activity size={20} className={`${getHealthColor(health)} mb-2`} />
                      <span className={`${themeClasses.textPrimary} font-semibold text-sm mb-1`}>Engine</span>
                      <span className={`text-3xl font-bold ${getHealthColor(health)}`}>
                        {health}%
                      </span>
                      {badge && (
                        <span className={`mt-2 px-2 py-1 rounded text-xs font-semibold border ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                      {distance > 0 && (
                        <span className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
                          {formatDistance(distance)} since service
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Suspension */}
              {(() => {
                const health = getComponentHealth('suspension');
                const serviceStatus = getServiceStatus('suspension');
                const serviceLevel = getServiceLevel('suspension');
                const distance = getDistanceSinceService('suspension');
                const badge = getServiceStatusBadge(serviceStatus, serviceLevel);
                return (
                  <div className={`p-4 rounded-lg border ${getHealthBgColor(health)} relative`}>
                    <div className="flex flex-col items-center text-center">
                      <Activity size={20} className={`${getHealthColor(health)} mb-2`} />
                      <span className={`${themeClasses.textPrimary} font-semibold text-sm mb-1`}>Suspension</span>
                      <span className={`text-3xl font-bold ${getHealthColor(health)}`}>
                        {health}%
                      </span>
                      {badge && (
                        <span className={`mt-2 px-2 py-1 rounded text-xs font-semibold border ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                      {distance > 0 && (
                        <span className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
                          {formatDistance(distance)} since service
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Brakes */}
              {(() => {
                const health = getComponentHealth('brakes');
                const serviceStatus = getServiceStatus('brakes');
                const serviceLevel = getServiceLevel('brakes');
                const distance = getDistanceSinceService('brakes');
                const badge = getServiceStatusBadge(serviceStatus, serviceLevel);
                return (
                  <div className={`p-4 rounded-lg border ${getHealthBgColor(health)} relative`}>
                    <div className="flex flex-col items-center text-center">
                      <Activity size={20} className={`${getHealthColor(health)} mb-2`} />
                      <span className={`${themeClasses.textPrimary} font-semibold text-sm mb-1`}>Brakes</span>
                      <span className={`text-3xl font-bold ${getHealthColor(health)}`}>
                        {health}%
                      </span>
                      {badge && (
                        <span className={`mt-2 px-2 py-1 rounded text-xs font-semibold border ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                      {distance > 0 && (
                        <span className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
                          {formatDistance(distance)} since service
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Transmission */}
              {(() => {
                const health = getComponentHealth('transmission');
                const serviceStatus = getServiceStatus('transmission');
                const serviceLevel = getServiceLevel('transmission');
                const distance = getDistanceSinceService('transmission');
                const badge = getServiceStatusBadge(serviceStatus, serviceLevel);
                return (
                  <div className={`p-4 rounded-lg border ${getHealthBgColor(health)} relative`}>
                    <div className="flex flex-col items-center text-center">
                      <Activity size={20} className={`${getHealthColor(health)} mb-2`} />
                      <span className={`${themeClasses.textPrimary} font-semibold text-sm mb-1`}>Transmission</span>
                      <span className={`text-3xl font-bold ${getHealthColor(health)}`}>
                        {health}%
                      </span>
                      {badge && (
                        <span className={`mt-2 px-2 py-1 rounded text-xs font-semibold border ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                      {distance > 0 && (
                        <span className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
                          {formatDistance(distance)} since service
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right Side - 1/3 width */}
        <div className="space-y-6">
          {/* Alert Status Card */}
          <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6`}>
            <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-4`}>Alert Status</h3>
            <div className="flex flex-col items-center text-center mb-4">
              <AlertTriangle size={48} className={vehicle.alertLevel === 'high' ? 'text-red-400' : vehicle.alertLevel === 'medium' ? 'text-amber-400' : 'text-green-400'} />
              <span className={`text-sm font-semibold mt-2 px-3 py-1 rounded-lg ${getAlertBadgeColor(vehicle.alertLevel)}`}>
                {vehicle.alertLevel.toUpperCase()}
              </span>
            </div>
            <div className={`border-t ${themeClasses.border} pt-4`}>
              <p className={`${themeClasses.textMuted} text-sm text-center mb-2`}>Overall Health</p>
              <p className={`text-5xl font-bold text-center ${getHealthColor(vehicle.health)}`}>
                {vehicle.health}%
              </p>
            </div>
          </div>

          {/* Service Due & Cumulative Wear Card */}
          <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6`}>
            <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-4 flex items-center gap-2`}>
              <BarChart3 size={20} className="text-blue-400" />
              Service Due & Component Wear
            </h3>
            
            {/* Service Due Graph */}
            <ServiceDueGraph
              key={`service-due-${vehicle.id}-${vehicle.health}-${JSON.stringify(vehicle.serviceDueAlarms)}`}
              componentHealth={vehicle.componentHealth}
              serviceDueAlarms={vehicle.serviceDueAlarms}
              vehicleType={vehicle.type}
              themeClasses={themeClasses}
            />
            
            {/* Divider */}
            <div className={`border-t ${themeClasses.border} my-4`}></div>
            
            {/* Cumulative Wear Graph */}
            <CumulativeWearGraph
              key={`cumulative-wear-${vehicle.id}-${vehicle.health}-${JSON.stringify(vehicle.lifespanAlarms)}`}
              componentHealth={vehicle.componentHealth}
              lifespanAlarms={vehicle.lifespanAlarms}
              vehicleType={vehicle.type}
              isMetric={isMetric}
              themeClasses={themeClasses}
            />
          </div>

        </div>
      </div>

      {/* Mission History - Full Width */}
      <VehicleMissionHistory vehicle={vehicle} themeClasses={themeClasses} />

      {/* Notes Section - Full Width */}
      {(vehicle.generalNotes || (vehicle.componentNotes && Object.values(vehicle.componentNotes).some(note => note))) && (
        <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mb-6`}>
          <h3 className={`text-xl font-bold ${themeClasses.textPrimary} mb-4`}>Vehicle Notes</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Component Notes */}
            {vehicle.componentNotes && Object.values(vehicle.componentNotes).some(note => note) && (
              <div>
                <h4 className={`text-lg font-semibold ${themeClasses.textPrimary} mb-3`}>Component Condition Notes</h4>
                <div className="space-y-3">
                  {vehicle.componentNotes.suspension && (
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-3`}>
                      <p className={`${themeClasses.textSecondary} text-sm font-medium mb-1`}>Suspension</p>
                      <p className={`${themeClasses.textPrimary} text-sm`}>{vehicle.componentNotes.suspension}</p>
                    </div>
                  )}
                  {vehicle.componentNotes.brakes && (
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-3`}>
                      <p className={`${themeClasses.textSecondary} text-sm font-medium mb-1`}>Brakes</p>
                      <p className={`${themeClasses.textPrimary} text-sm`}>{vehicle.componentNotes.brakes}</p>
                    </div>
                  )}
                  {vehicle.componentNotes.engine && (
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-3`}>
                      <p className={`${themeClasses.textSecondary} text-sm font-medium mb-1`}>Engine</p>
                      <p className={`${themeClasses.textPrimary} text-sm`}>{vehicle.componentNotes.engine}</p>
                    </div>
                  )}
                  {vehicle.componentNotes.transmission && (
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-3`}>
                      <p className={`${themeClasses.textSecondary} text-sm font-medium mb-1`}>Transmission</p>
                      <p className={`${themeClasses.textPrimary} text-sm`}>{vehicle.componentNotes.transmission}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* General Notes */}
            {vehicle.generalNotes && (
              <div>
                <h4 className={`text-lg font-semibold ${themeClasses.textPrimary} mb-3`}>General Notes</h4>
                <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
                  <p className={`${themeClasses.textPrimary} text-sm whitespace-pre-wrap`}>{vehicle.generalNotes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Maintenance History - Full Width at Bottom */}
      <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-bold ${themeClasses.textPrimary}`}>Maintenance History</h3>
          <button
            onClick={() => setShowMaintenanceModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <Plus size={16} />
            Add Maintenance
          </button>
        </div>
        <div className="space-y-4">
          {[...vehicle.maintenanceHistory].sort((a, b) => new Date(b.date) - new Date(a.date)).map((record, index) => {
            // Parse component details if available
            let componentDetails = null;
            try {
              componentDetails = record.componentDetails ? JSON.parse(record.componentDetails) : null;
            } catch (e) {
              componentDetails = null;
            }

            // Get components that have maintenance data (maintenance date set)
            const maintainedComponents = [];
            if (componentDetails) {
              Object.entries(componentDetails).forEach(([componentName, data]) => {
                if (data.maintenanceDate) {
                  maintainedComponents.push({ name: componentName, data });
                }
              });
            }

            return (
              <div
                key={index}
                className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6 hover:${themeClasses.card} transition-all`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getMaintenanceIcon(record.details)}
                    <div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-blue-400" />
                        <span className={`${themeClasses.textPrimary} font-semibold`}>{formatDateShort(record.date)}</span>
                      </div>
                      <p className={`${themeClasses.textMuted} text-sm mt-1`}>
                        Technician: {record.technicianId} | Mileage: {record.mileageAtMaintenance.toLocaleString()} mi
                      </p>
                    </div>
                  </div>
                </div>

                {/* General Notes */}
                {record.details && (
                  <div className="mb-4">
                    <h4 className={`${themeClasses.textPrimary} font-medium text-sm mb-2`}>General Notes</h4>
                    <p className={`${themeClasses.textSecondary} text-sm bg-gray-50 dark:bg-gray-800 rounded-lg p-3`}>
                      {record.details}
                    </p>
                  </div>
                )}

                {/* Component Details */}
                {maintainedComponents.length > 0 && (
                  <div>
                    <h4 className={`${themeClasses.textPrimary} font-medium text-sm mb-3`}>
                      Component Maintenance ({maintainedComponents.length} component{maintainedComponents.length > 1 ? 's' : ''})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {maintainedComponents.map(({ name, data }) => (
                        <div key={name} className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                          {/* Component Header */}
                          <div className="flex items-center gap-2 mb-3">
                            {name === 'suspension' && <Activity size={16} className="text-green-400" />}
                            {name === 'brakes' && <Shield size={16} className="text-red-400" />}
                            {name === 'engine' && <Cog size={16} className="text-blue-400" />}
                            {name === 'transmission' && <Droplet size={16} className="text-purple-400" />}
                            <span className={`${themeClasses.textPrimary} font-semibold capitalize`}>{name}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              data.health >= 90 ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                              data.health >= 80 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                              'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                            }`}>
                              {typeof data.health === 'object' ? data.health.health : data.health}%
                            </span>
                          </div>

                          {/* Component Details */}
                          <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                              <span className={themeClasses.textMuted}>Maintenance Date:</span>
                              <span className={themeClasses.textPrimary}>{formatDateShort(data.maintenanceDate)}</span>
                            </div>
                            
                            {data.mileageSinceService > 0 && (
                              <div className="flex justify-between">
                                <span className={themeClasses.textMuted}>Mileage Since Service:</span>
                                <span className={themeClasses.textPrimary}>{data.mileageSinceService.toLocaleString()} mi</span>
                              </div>
                            )}

                            {data.technician && (
                              <div className="flex justify-between">
                                <span className={themeClasses.textMuted}>Technician:</span>
                                <span className={themeClasses.textPrimary}>{data.technician}</span>
                              </div>
                            )}

                            {/* Component-specific fields */}
                            {name === 'brakes' && (
                              <>
                                {data.padThickness && (
                                  <div className="flex justify-between">
                                    <span className={themeClasses.textMuted}>Pad Thickness:</span>
                                    <span className={themeClasses.textPrimary}>{data.padThickness}</span>
                                  </div>
                                )}
                                {data.fluidLevel && (
                                  <div className="flex justify-between">
                                    <span className={themeClasses.textMuted}>Fluid Level:</span>
                                    <span className={themeClasses.textPrimary}>{data.fluidLevel}</span>
                                  </div>
                                )}
                              </>
                            )}

                            {name === 'engine' && data.oilQuality && (
                              <div className="flex justify-between">
                                <span className={themeClasses.textMuted}>Oil Quality:</span>
                                <span className={themeClasses.textPrimary}>{data.oilQuality}</span>
                              </div>
                            )}

                            {name === 'transmission' && data.shiftQuality && (
                              <div className="flex justify-between">
                                <span className={themeClasses.textMuted}>Shift Quality:</span>
                                <span className={themeClasses.textPrimary}>{data.shiftQuality}</span>
                              </div>
                            )}

                            {/* Condition Notes */}
                            {data.conditionNotes && (
                              <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <span className={`${themeClasses.textMuted} text-xs`}>Notes:</span>
                                <p className={`${themeClasses.textSecondary} text-xs mt-1 italic`}>
                                  "{data.conditionNotes}"
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show message if no component details */}
                {maintainedComponents.length === 0 && (
                  <div className={`${themeClasses.textMuted} text-sm italic text-center py-2`}>
                    No component-specific maintenance details recorded
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Vehicle Modal */}
      <EditVehicleModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        vehicle={vehicle}
        onVehicleUpdated={handleVehicleUpdated}
      />

      {/* Add Maintenance Modal */}
      <AddMaintenanceModal
        isOpen={showMaintenanceModal}
        onClose={() => setShowMaintenanceModal(false)}
        vehicle={vehicle}
        onMaintenanceAdded={handleMaintenanceAdded}
      />
    </div>
  );
};

export default VehicleDetail;
